import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import type {
  ColumnFilterOption,
  ColumnFilterProps,
  FilterableTHProps,
} from '@/components/ui/ColumnFilter';
import { cx } from '@/lib/cx';
import { EmailLink } from '@/components/ui/TextLink';
import { formatDateTime, orDash } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import { isAdminRole, roleLabel } from '@/lib/roles';
import type { Paginated, User, UserRole, UserSort } from '@/lib/types';
import { groupsOf, useUserGroups } from '@/lib/user-groups';
import { InlineUserGroups } from '@/components/users/InlineUserGroups';
import { SupportSessionDialog } from '@/components/support/SupportSessionDialog';
import { canOfferSupportSessions, supportSessionDenial } from '@/lib/support-session';
import { useAuth } from '@/lib/use-auth';
import { useTableFilters, type ColumnSpec, type TableFilters } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { OnboardingEmailModal } from '@/components/users/OnboardingEmailModal';
import { UserFormModal } from '@/components/users/UserFormModal';
import { UserTargetsModal } from '@/components/users/UserTargetsModal';
import {
  Avatar,
  Badge,
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  THead,
  TR,
  type FrozenSide,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * ⚠️ `PER_PAGE = 10` WAS HERE and is gone with the pager (owner's instruction,
 * 12 September 2026). The page size that remains is `api.listAllUsers`' own
 * `perPage: 100`, which is the SERVER's ceiling rather than a display choice —
 * do not reintroduce a display page size here.
 */
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

/*
 * The Role funnel's options — the complete five, declared rather than derived.
 *
 * ⚠️ **A `scope: 'server'` column offers only what it has already SEEN unless
 * it declares `options`.** The hook accumulates values out of loaded rows, so
 * on a directory read ten to a page the funnel offered whichever two or three
 * roles page 1 happened to hold — and the role somebody wants to find is
 * usually the rare one (the single `super_admin`, the one `viewer`), which is
 * exactly what is missing from page 1. Worse, once a role WAS picked the next
 * response contained that role alone, so the accumulated list could not grow
 * back and the filter was effectively one-way.
 *
 * `role` is a CLOSED SET the API defines — `UserListParams.role` takes one of
 * five slugs and a stray value is a 422 — which is precisely the case
 * `ColumnSpec.options` exists for. There is no fetch behind it, so unlike a
 * master-backed list this one has no "the read failed" state to degrade from.
 *
 * ⚠️ **Ordered by CAPABILITY TIER, not alphabetically.** `ColumnSpec.options`
 * preserves the order it is given for this reason: read-only, then write, then
 * the tier that may also delete and manage. Sorting by label would open with
 * `Accounts` and close with `University Partnerships`, which ranks nothing.
 *
 * ⚠️ **A `Record<UserRole, number>` rather than an array**, so a sixth role
 * added to `UserRole` is a `tsc` error here rather than a slug silently absent
 * from a list that claims to be complete — the very defect this constant
 * removes, arriving through the back door.
 */
const ROLE_TIER_ORDER: Record<UserRole, number> = {
  viewer: 0,
  basic_user: 1,
  advanced_user: 2,
  admin: 3,
  super_admin: 4,
};

/*
 * ⚠️ **Values are the SLUGS and labels come from `roleLabel`.** The slug is
 * what `?role=` takes and the label is what the business calls the tier; the
 * two were renamed apart on 2026-09-02 and `roleLabel` is the one place that
 * mapping lives, so the funnel, the badge in the cell and the hook's own
 * fallback cannot word a role three ways. Never `String(role)` — casing a slug
 * gives "Basic_user".
 *
 * Sorted through `ROLE_TIER_ORDER` rather than relying on the key order of the
 * object literal, so the sequence is stated once and cannot drift if somebody
 * reorders the record for another reason.
 */
const ROLE_FILTER_OPTIONS: ColumnFilterOption[] = (Object.keys(ROLE_TIER_ORDER) as UserRole[])
  .sort((a, b) => ROLE_TIER_ORDER[a] - ROLE_TIER_ORDER[b])
  .map((role) => ({ value: role, label: roleLabel(role) }));

/*
 * `/users` supports `sort` (name | email | createdAt | updatedAt | role | groups
 * | id), `order` and `role` server-side, so `name`, `role`, `groups` and `id`
 * each carry a real `sortKey` and `role` is a real server-scoped filter.
 * *SUPERSEDED 2026-10-09: "`id` stays page-scoped and unsorted: it is not in the
 * server's sort enum"* — backend `32057ed` added `role` (tier rank, Operations →
 * Super Admin), `groups` (membership count) and `id`.
 *
 * ⚠️ **`name` stays page-scoped too, and that is a decision.** The nearest
 * server param is `?search=`, which matches name, email OR designation — and
 * the search box at the top of this panel already writes it. Pointing this
 * column at it would make two writers of one query key, the failure
 * `InvoicesPage`'s Status column is annotated at length to prevent: whichever
 * spread ran last would win and the other control would keep displaying a value
 * the request never carried.
 */
const USER_COLUMNS: ColumnSpec<User>[] = [
  { key: 'name', label: 'User', get: (row) => row.name, sortKey: 'name' satisfies UserSort },
  {
    key: 'role',
    label: 'Role',
    get: (row) => row.role,
    scope: 'server',
    param: 'role',
    // `?role=` takes a comma-separated list (2026-10-28).
    multi: true,
    // The server orders by tier rank (Operations → Super Admin), never by slug.
    sortKey: 'role' satisfies UserSort,
    // The complete five — see ROLE_FILTER_OPTIONS. Declaring this REPLACES the
    // values the hook accumulates from the loaded page, which is the point: one
    // tick now queries the whole directory, and every role is offered whether or
    // not anybody on page 1 holds it.
    options: ROLE_FILTER_OPTIONS,
    /*
     * Kept even though `options` now supplies every label the popover renders.
     * It is what the hook falls back to for the accumulated list, and it is the
     * same `roleLabel` the options are built from — so the two can never
     * disagree, and dropping `options` later would degrade rather than break.
     */
    format: roleLabel,
  },
  /*
   * Groups.
   *
   * A SET, in a model that has one scalar per cell, so `get` is not what decides
   * matching here — `matches` is. What `get` is still for: the emptiness test
   * (`null` only when the account is in no group at all) and a value for the
   * hook's own option list, which the page then SUBSTITUTES from the master,
   * because a joined "1,3" is not an option anybody would tick.
   *
   * `multi` since 2026-10-28: `?groupId=` takes a comma-separated list (any of
   * the groups), so every tick queries the whole directory server-side.
   * `matches` stays for the hook's page-scoped fallback.
   * *SUPERSEDED: "No `multi`. `?groupId=` takes a SINGLE id."*
   */
  {
    key: 'groups',
    label: 'Groups',
    get: (row) => {
      const groups = groupsOf(row);
      return groups.length === 0 ? null : groups.map((group) => group.id).join(',');
    },
    matches: (row, selected) =>
      groupsOf(row).some((group) => selected.includes(String(group.id))),
    scope: 'server',
    param: 'groupId',
    multi: true,
    // Orders by how many groups the account is in — a set has no single value.
    sortKey: 'groups' satisfies UserSort,
  },
  { key: 'id', label: 'ID', get: (row) => row.id, numeric: true, sortKey: 'id' satisfies UserSort },
];

/**
 * The keys that HAVE a `ColumnSpec`, and therefore a funnel in the header.
 *
 * ⚠️ **Derived, never listed.** The header cell branches on this: a key in the
 * set is spread from `headerFor`, and anything else gets a plain label. Written
 * out by hand it would be a third place a column has to be registered, and the
 * failure of forgetting is silent in both directions — a spec'd column with a
 * plain header simply loses its funnel, and a spec-less column handed
 * `headerFor` gets an entry the hook never made.
 */
const FILTERED_COLUMN_KEYS = new Set<string>(USER_COLUMNS.map((spec) => spec.key));

/** The layout scope, reusing this page's saved-filter scope string. */
const COLUMN_SCOPE = 'users';

const ACTIONS_KEY = 'actions';
const ACTIONS_LABEL = 'Actions';

/**
 * Last active — when this person last did something real, from the audit log.
 *
 * ⛔ **NO `ColumnSpec`, AND THAT IS THE POINT — it is the second column here
 * with no funnel and no sort arrow, after `actions`.** A spec buys a funnel,
 * and a `sortKey` buys a sort arrow, and `/users` can serve neither:
 * `UserListParams` declares `page`, `perPage`, `search`, `role`, `groupId`,
 * `sort` and `order` and **nothing else**, while `UserSort` is
 * `name | email | createdAt | updatedAt`. The list query schema is strict, so
 * `?sort=lastActiveAt` is a **422 that blanks the table** rather than a list
 * that merely comes back unsorted — and a page-scoped funnel over rendered
 * relative text would offer "3 hours ago" and "yesterday" as tick boxes, a
 * vocabulary that changes every time the page is opened.
 *
 * `FilterableTH` claims `aria-sort` only where the API can really sort, so a
 * plain header promises nothing this column cannot do. **Give it a spec the day
 * `/users` accepts a matching param, and not one moment before** — this repo
 * has shipped four filters that parsed, typechecked, answered `200` and
 * returned the entirely unfiltered list.
 */
const LAST_ACTIVE_KEY = 'lastActiveAt';
const LAST_ACTIVE_LABEL = 'Last active';

/**
 * One column of the desktop table.
 *
 * `minWidth` is this column's share of the table's width FLOOR, in px — see
 * `USERS_TABLE_COLUMNS`. `className` is the width request on the header cell,
 * absent on the one fluid column.
 */
interface UsersColumn {
  key: string;
  minWidth: number;
  className?: string;
  numeric?: boolean;
}

/**
 * 🔴 **THE ONE ORDERED COLUMN LIST.** The header, the body, the skeleton, the
 * width floor and the column manager all read this array and nothing else. A
 * skeleton with a different column count from the real table is a visible
 * layout jump the moment rows land, which is what three separate hand-kept
 * lists used to risk here.
 *
 * ⚠️ **`actions` HAS NO `ColumnSpec`, AND MUST STILL BE MODELLED HERE.**
 * `USER_COLUMNS` is the FILTER model — a spec needs a `get`, and the actions
 * lane has no value to filter or sort on. It is nonetheless a real column on
 * screen, so a key list built from `USER_COLUMNS` alone would leave it out of
 * the manager (a column nobody can freeze, listed nowhere) and out of the width
 * floor (which would then be 224px short of the table it describes).
 *
 * ⚠️ **`minWidth` IS A FLOOR, NOT A `<colgroup>` SUM.** This table has no
 * `table-fixed`, so a `w-*` on a `<th>` is a request the content can outvote —
 * `/projects` records the same distinction. The numbers sum to **896**
 * (128 + 128 + 208 + 80 + 128 + 224), the first being the remainder left to the
 * fluid User column once the five declared widths are taken out. ⚠️ **Nothing
 * hand-writes that total** — `tableMinWidth` reduces this array, so the figure
 * here is prose for a reader and the code cannot disagree with it. Their only
 * job is to let the floor SHRINK as columns are hidden; move one whenever a
 * `w-*` beside it moves. (It was 768 over five columns before Last active.)
 */
const USERS_TABLE_COLUMNS: readonly UsersColumn[] = [
  { key: 'name', minWidth: 128 },
  { key: 'role', minWidth: 128, className: 'w-32' },
  /* w-52: two chips ("Account lead", "Client success manager") wrap to two rows
     rather than widening the track. */
  { key: 'groups', minWidth: 208, className: 'w-52' },
  { key: 'id', minWidth: 80, className: 'w-20', numeric: true },
  /* w-32: the cell renders RELATIVE text, not a timestamp — "11 minutes ago"
     is the longest ordinary rendering at ~95px, and the header is shorter than
     that. Sized for the text rather than for the absolute instant, which lives
     on the `title` and never occupies the column. Not `numeric`: it is prose,
     so it reads left-aligned like User and Role rather than right-aligned like
     ID. */
  { key: LAST_ACTIVE_KEY, minWidth: 128, className: 'w-32' },
  /* w-64: the cell seats SIX icon buttons (Targets, Act as, Onboarding email,
     Notifications, Edit, Delete) plus TD's own px-3 on both sides. Each widening has had the
     same cause — a button added to the cell — and the same symptom if skipped:
     the column overruns its declared width and ID beside it pays the
     difference. */
  { key: ACTIONS_KEY, minWidth: 256, className: 'w-64', numeric: true },
];

const COLUMN_BY_KEY = new Map<string, UsersColumn>(
  USERS_TABLE_COLUMNS.map((column): [string, UsersColumn] => [column.key, column]),
);
const ALL_COLUMN_KEYS: readonly string[] = USERS_TABLE_COLUMNS.map((column) => column.key);

/**
 * ⛔ **GROUPS AND ACTIONS MAY NEVER BE HIDDEN.**
 *
 * `actions` is the ordinary case: the lane carries five controls, three of
 * which (Act as, the onboarding email, Targets) exist on no other screen.
 *
 * 🔴 **`groups` is the one worth stating.** That cell hosts `InlineUserGroups`,
 * and membership is edited **on the list row and nowhere else** — there is no
 * `/users/:id` route and `UserFormModal`'s `groupIds` is a create-time field.
 * So hiding this column does not tidy a view, it **removes the only way to
 * change who is in a user group**, and the reader who hid it has no reason to
 * connect the missing capability to a column they turned off weeks ago. The
 * sub-`md` card list carries the same editor for the same reason.
 *
 * Module scope, so the reference is stable across renders — `useColumnLayout`
 * memoises a `Set` on it.
 */
const LOCKED_COLUMN_KEYS: readonly string[] = ['groups', ACTIONS_KEY];

/**
 * Why each locked column is locked, in the reader's own terms.
 *
 * ⚠️ **THE SHARED DEFAULT IS "This column cannot be hidden", WHICH IS TRUE AND
 * USELESS HERE.** `ColumnManager` takes this because one sentence cannot cover
 * every lock, and the two on this page are locked for genuinely different
 * reasons: the actions lane is the ordinary "it holds the controls" case, while
 * `groups` is locked because hiding it **removes the only way to change who is
 * in a user group** — see `LOCKED_COLUMN_KEYS`. A reader who is told the Group
 * column "cannot be hidden" learns nothing about why, and the generic sentence
 * would read as arbitrary on the one column where the reason is worth knowing.
 *
 * ⚠️ **These are `title` strings on a disabled control — a *why this is
 * disabled* sentence, which is one of the four kinds the no-helper-text
 * prohibition explicitly keeps.** Without them a locked switch and a broken one
 * are indistinguishable.
 *
 * Returns `undefined` for anything unlocked, so the manager falls back to its
 * own default rather than this map inventing a reason for a column that has
 * none.
 */
const LOCK_REASONS = new Map<string, string>([
  [
    'groups',
    'Group membership is edited here and nowhere else, so hiding this column would remove the only way to change it',
  ],
  [ACTIONS_KEY, 'Every per-user control lives in this column'],
]);

function lockReasonOf(key: string): string | undefined {
  return LOCK_REASONS.get(key);
}

/**
 * The header label per key.
 *
 * ⚠️ **Read out of `USER_COLUMNS` rather than restated**, so the manager cannot
 * come to call a column something the header does not. Only the two columns
 * with no spec — `actions` and `lastActiveAt` — carry their labels here, and
 * both are the same constants the header cell renders.
 */
const COLUMN_LABELS = new Map<string, string>([
  ...USER_COLUMNS.map((spec): [string, string] => [spec.key, spec.label]),
  [LAST_ACTIVE_KEY, LAST_ACTIVE_LABEL],
  [ACTIONS_KEY, ACTIONS_LABEL],
]);

function columnLabel(key: string): string {
  return COLUMN_LABELS.get(key) ?? key;
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/* `ChevronIcon` was deleted here with the Previous/Next buttons it drew — its
 * only caller. A component kept for a caller that has gone is dead code that
 * reads as a capability still in use. */

function TargetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

/** A life ring — the support-session action. */
function LifebuoyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="m5.6 5.6 3.85 3.85" />
      <path d="m14.55 14.55 3.85 3.85" />
      <path d="m18.4 5.6-3.85 3.85" />
      <path d="m9.45 14.55-3.85 3.85" />
    </svg>
  );
}

/** An envelope — the onboarding-email action. */
function MailIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/* ── Relative time ─────────────────────────────────────────────────────────
 *
 * ⚠️ **THERE IS NO RELATIVE-TIME HELPER IN `src/lib/format.ts`** — it carries
 * `toDateInputValue`, `formatDateOnly`, `formatDateTime` and `orDash`, and
 * nothing that says "3 hours ago". Checked before writing this.
 *
 * The nearest thing in the app is `relativeDue` in `src/lib/task-time.ts`, and
 * it is **not reusable here**: it takes a `TaskDue` — a wall-clock reading plus
 * the ASSIGNEE's IANA zone — rather than an instant, and it is built around a
 * FUTURE due date, which is why it sets `numeric: 'always'` on purpose and can
 * never say "yesterday". `lastActiveAt` is a past instant read in the VIEWER's
 * own zone. Two different questions, so this stays local to the one page that
 * asks it rather than becoming a shared module nobody asked for.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** One instance per mode, reused: this formats once per row. */
function relativeFormatter(numeric: 'always' | 'auto'): Intl.RelativeTimeFormat | null {
  try {
    return new Intl.RelativeTimeFormat(undefined, { numeric });
  } catch {
    return null;
  }
}
/** "yesterday", "today" — the named forms. */
const RELATIVE_NAMED = relativeFormatter('auto');
/** "2 weeks ago", "3 hours ago" — always a number. */
const RELATIVE_COUNTED = relativeFormatter('always');

function formatRelative(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  formatter: Intl.RelativeTimeFormat | null,
): string {
  if (formatter) {
    try {
      return formatter.format(value, unit);
    } catch {
      /* falls through to the plain rendering below */
    }
  }
  /* No `Intl.RelativeTimeFormat` on this engine. `value` is never positive
     here — this column only ever looks backwards — so "ago" is always right. */
  if (value === 0) return 'today';
  const magnitude = Math.abs(value);
  return `${magnitude} ${magnitude === 1 ? unit : `${unit}s`} ago`;
}

/** The start of the local calendar day `at` falls on, as epoch ms. */
function localDayStart(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

/**
 * "3 hours ago", "yesterday", "2 weeks ago" — or `null` when the stamp cannot
 * be read at all.
 *
 * ⚠️ **`lastActiveAt` IS A REAL ISO TIMESTAMP, SO `new Date(iso)` IS SAFE
 * HERE.** The app-wide prohibition is about date-ONLY columns — a bare
 * `'2026-05-31'` parses as midnight UTC and renders as the 30th for anyone west
 * of Greenwich, which is what `formatDateOnly` and `toDateInputValue` exist to
 * avoid. An instant carries its own offset and is MEANT to be localised, which
 * is exactly what `formatDateTime` (the `title` below) does with it.
 *
 * ⚠️ **The day band counts LOCAL CALENDAR DAYS, not 24-hour blocks**, and that
 * is what lets it say "yesterday" honestly: something 23 hours old is
 * "yesterday" only if it really fell on the previous local day, and otherwise
 * reads "today". Dividing by 24 hours says "yesterday" for both. `Math.round`
 * over the two midnights absorbs the 23- and 25-hour days daylight saving
 * produces.
 *
 * ⚠️ **Only the day band uses `numeric: 'auto'`.** Its other named forms —
 * "last week", "last month" — describe calendar periods this function does not
 * compute (it divides elapsed days), so they would be claims it has not
 * checked. Every other band counts.
 *
 * ⚠️ **A stamp in the FUTURE reads "just now", never "in 2 hours".** Browser and
 * server clocks disagree by seconds routinely and a *last active* time cannot
 * genuinely be ahead of now, so the only honest reading of a negative elapsed
 * is that it has only just happened. The `title` still carries the absolute, so
 * a badly skewed clock stays visible rather than being papered over.
 */
function relativeSince(iso: string, now: Date = new Date()): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const elapsed = now.getTime() - at.getTime();
  if (elapsed < 45_000) return 'just now';
  if (elapsed < 45 * MINUTE_MS) {
    return formatRelative(-Math.round(elapsed / MINUTE_MS), 'minute', RELATIVE_COUNTED);
  }
  if (elapsed < 22 * HOUR_MS) {
    return formatRelative(-Math.round(elapsed / HOUR_MS), 'hour', RELATIVE_COUNTED);
  }

  const days = Math.round((localDayStart(now) - localDayStart(at)) / DAY_MS);
  if (days < 7) return formatRelative(-days, 'day', RELATIVE_NAMED);
  if (days < 35) return formatRelative(-Math.round(days / 7), 'week', RELATIVE_COUNTED);
  if (days < 320) return formatRelative(-Math.round(days / 30.44), 'month', RELATIVE_COUNTED);
  return formatRelative(-Math.round(days / 365.25), 'year', RELATIVE_COUNTED);
}

/**
 * When this person last did something real — relative on screen, the exact
 * instant on the `title`.
 *
 * That split is the established pattern for a dense cell whose full value still
 * matters: `/projects` puts the bare city name in the chip and its country on
 * the cell's `title`, for the same reason. "3 hours ago" is what somebody scans
 * a directory for; the timestamp is what they need once they have stopped.
 *
 * 🔴 **`null` IS NOT "NEVER", AND MUST NEVER RENDER AS IT.** It means the audit
 * log holds no QUALIFYING activity for this account — routine noise such as
 * token renewals is excluded server-side, so an account can have been signed
 * into and still have nothing here. Six `viewer` accounts exist and **nobody
 * has measured whether any of them has ever been signed into**; "Never" would
 * answer a question this column has not asked and cannot answer, and it would
 * answer it about a colleague. It draws an em dash through `orDash`, the page's
 * own empty convention, and the distinction lives on the `title`.
 *
 * ⚠️ **An UNREADABLE stamp gets its own `title` rather than sharing the `null`
 * one.** Both draw an em dash — there is nothing else honest to draw — but "no
 * qualifying activity" and "this value did not parse as a date" are different
 * facts, and collapsing them would hide a contract change behind a cell that
 * looks ordinarily empty.
 */
function LastActive({ at }: { at: string | null }) {
  if (at === null) {
    return (
      <span
        className="text-fg-subtle"
        title="No qualifying activity in the audit log. Routine requests such as token renewals are not counted, so this does not mean the account has never been used."
      >
        {orDash(null)}
      </span>
    );
  }

  const relative = relativeSince(at);
  if (relative === null) {
    return (
      <span
        className="text-fg-subtle"
        title={`The recorded value could not be read as a date: ${at}`}
      >
        {orDash(null)}
      </span>
    );
  }

  return (
    <span className="whitespace-nowrap" title={formatDateTime(at)}>
      {relative}
    </span>
  );
}

/*
 * The API stores slugs (`super_admin`); `roleLabel` renders them because CSS
 * `capitalize` only touches the first letter of each word.
 *
 * Two treatments for five roles, on purpose. A soft neutral fill marks the
 * admin tier — the same predicate that decides whether the delete control next
 * to it renders — and everything else is a quiet outlined tag. ⚠️ *SUPERSEDED
 * 2026-10-11: the admin tier was `accent`*; accent is reserved for primary
 * actions, the active nav row and focus, so a role label may not use it. Five
 * treatments would imply five permission
 * levels; there are two, and `basic_user` / `advanced_user` (and `admin` /
 * `super_admin`) are indistinguishable today. The label carries the rest, so
 * colour is never the only signal.
 */
function RoleBadge({ role }: { role: UserRole }) {
  return (
    <Badge tone="neutral" variant={isAdminRole(role) ? 'soft' : 'outline'}>
      {roleLabel(role)}
    </Badge>
  );
}

/** Avatar plus the name over the email — the identity cell in both renderings. */
function UserIdentity({ row, size }: { row: User; size: 'sm' | 'md' }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar name={row.name} size={size} />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-fg">{row.name}</p>
        {/* Emails have no spaces, so only anywhere-wrapping keeps them inside 320px. */}
        <p className="text-xs [overflow-wrap:anywhere]">
          <EmailLink address={row.email} tone="muted" />
        </p>
      </div>
    </div>
  );
}

/** The frozen props for one cell, as a spread — `undefined` means "not frozen". */
interface FrozenCellProps {
  frozen?: FrozenSide;
  frozenOffset?: number;
}

/**
 * The scroller, plus the width FLOOR.
 *
 * ⛔ **THE FLOOR IS AN INLINE `style`, AND IT CANNOT BE A CLASS.** It shrinks as
 * columns are hidden, and **Tailwind scans SOURCE TEXT, so `min-w-[${n}px]`
 * emits no rule at all** — the class lands in the DOM and does nothing, which
 * looks exactly like a table that chose not to have a floor.
 * `UniversityPartnershipsPage` records the same trap.
 *
 * ✅ **It sits on `Table` itself.** *SUPERSEDED: "It sits on a `<div>` rather
 * than on `Table`, because `Table` takes no `style` — it is a shared primitive
 * and widening it is not this page's change to make."* `Table` takes a `style`
 * now, added on 2026-09-12 for exactly this, so the spacer `<div>` is gone and
 * the floor is back where the `min-w-[768px]` literal used to live — on the
 * table element.
 *
 * ⚠️ **Removing that div also removed a trap worth remembering if one ever
 * comes back.** A frozen cell is `position: sticky`, which resolves against its
 * nearest SCROLLING ancestor and lives inside its nearest stacking context, so
 * any wrapper introduced between `TableWrap` and `Table` must stay a bare block
 * — an `overflow`, a `transform`, an `opacity` or a `z-index` on it silently
 * moves both. Nothing errors: the cells keep the classes and the offsets they
 * were given and simply stop sticking.
 */
function TableFrame({ minWidth, children }: { minWidth: number; children: ReactNode }) {
  return (
    <TableWrap frozenHeader>
      <Table style={{ minWidth }}>{children}</Table>
    </TableWrap>
  );
}

/** The shimmer for one column — keyed the same way the real cell is. */
function SkeletonCell({ columnKey, actionCount }: { columnKey: string; actionCount: number }) {
  switch (columnKey) {
    case 'name':
      return (
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-6 w-6 rounded-full" />
          <div className="grid gap-1.5">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2.5 w-44" />
          </div>
        </div>
      );
    case 'role':
      return <Skeleton className="h-4 w-14" />;
    case 'groups':
      return <Skeleton className="h-4 w-24" />;
    case 'id':
      return <Skeleton className="ml-auto h-3 w-8" />;
    case LAST_ACTIVE_KEY:
      /* w-20: roughly "3 hours ago" at text-sm. Not `ml-auto` — the real cell
         is prose and left-aligned, and a shimmer on the other edge is a
         visible jump the moment rows land. */
      return <Skeleton className="h-3 w-20" />;
    case ACTIONS_KEY:
      return (
        <div className="flex justify-end gap-1.5">
          {Array.from({ length: actionCount }, (_, button) => (
            <Skeleton key={button} className="h-6 w-6" />
          ))}
        </div>
      );
    default:
      return null;
  }
}

/**
 * `actionCount` exists so the shimmer has the same number of buttons the real
 * cell will have. A skeleton whose actions column is narrower than the table's
 * is a visible layout jump the moment the rows land, which reads as the page
 * re-laying itself out rather than as data arriving.
 *
 * ⚠️ **It walks the SAME visible-column list the real body walks**, so a hidden
 * or reordered column cannot leave the two disagreeing — the layout jump above,
 * one rung worse.
 */
function TableSkeleton({
  columns,
  actionCount,
  frozenPropsFor,
}: {
  columns: readonly UsersColumn[];
  actionCount: number;
  frozenPropsFor: (key: string) => FrozenCellProps;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columns.map((column) => (
            <TD key={column.key} numeric={column.numeric} {...frozenPropsFor(column.key)}>
              <SkeletonCell columnKey={column.key} actionCount={actionCount} />
            </TD>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-2.5 w-40" />
          </div>
          <Skeleton className="h-4 w-12" />
        </li>
      ))}
    </ul>
  );
}

export function UsersPage() {
  const { user, ability, refreshUser, supportSession } = useAuth();
  const [result, setResult] = useState<Paginated<User> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  /** The row being edited, or null for the create form. */
  const [editing, setEditing] = useState<User | null>(null);
  /** The row whose targets are open, or null. See the note on `showRowActions`. */
  const [targetsFor, setTargetsFor] = useState<User | null>(null);
  /** The row a support session is being started for, or null. */
  const [supportFor, setSupportFor] = useState<User | null>(null);
  /**
   * The row whose onboarding email is being composed, or null.
   *
   * Reached two ways, and both land here: the row action, and a successful
   * create — see `handleSaved`. One piece of state rather than two, so a newly
   * created account and an existing one open the identical screen; a separate
   * "just created" variant would be a second place for the send's wording to
   * drift, and what would drift is a sentence about an email that leaves SEED.
   */
  const [onboardingFor, setOnboardingFor] = useState<User | null>(null);

  /*
   * `GET /users` and `GET /users/:id` carry no role guard at all, so every
   * authenticated account — `viewer` included — may read the staff list, even
   * though only the administrator permission set grants `users.read`.
   * `ability.canRead('users')` accounts for that, which is why this page is
   * never hidden; only its write controls are.
   *
   * Deleting a user is admin-only, and this is enforced server-side however
   * generous the permission set looks.
   */
  const canDeleteUsers = ability.canDelete('users');
  /*
   * Create and edit are graded independently of delete by the server, so each
   * control is gated on the ability it actually needs rather than on one
   * "is an admin" flag. Absent rather than present-and-403, matching delete.
   */
  const canCreateUsers = ability.canCreate('users');
  const canUpdateUsers = ability.canUpdate('users');
  const navigate = useNavigate();
  const openNotifications = (row: User) => navigate(`/notifications?userId=${row.id}`);
  /*
   * 2026-10-02 (owner): reset password, change role and the onboarding email are
   * `users.manage-access`. Ordinary edits stay `users.update`.
   */
  const canManageAccess = ability.canManageAccess();
  const rows = useMemo(() => result?.data ?? [], [result]);

  /*
   * `name` sorts server-side and `role` filters server-side; `id` is page-scoped
   * and its popover says so. `visibleRows` is what the table renders either way,
   * since page-scoped emptiness still applies to the loaded rows.
   */
  const filters = useTableFilters(rows, USER_COLUMNS);
  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;

  /*
   * The group master, for the Groups funnel.
   *
   * Read by every authenticated role — `GET /user-groups` has no role guard, the
   * same as `GET /users` above it — so this is not gated on a write ability.
   * Unpaginated, so there is no page to chase.
   */
  const groups = useUserGroups();

  /**
   * The Groups funnel's options.
   *
   * They come from the MASTER rather than from the loaded rows, because the
   * hook can only derive them from `get`, whose cell here is a joined set. Any
   * ticked id the master does not carry is appended so it still has a checkbox:
   * without one the column reads as filtered with no way to undo it but "Clear
   * column filters", which is the deactivation trap in filter form.
   */
  const groupFilterOptions = useCallback(
    (selected: readonly string[]): ColumnFilterOption[] => {
      const options: ColumnFilterOption[] = groups.records.map((group) => ({
        value: String(group.id),
        label: group.name,
      }));
      const known = new Set(options.map((option) => option.value));
      for (const value of selected) {
        if (known.has(value)) continue;
        known.add(value);
        options.push({ value, label: `Group #${value}` });
      }
      return options;
    },
    [groups.records],
  );

  /*
   * The hook's headers, with two things folded in: the page reset on every
   * column, and the Groups options on one. Not memoised: the hook returns a
   * fresh object each render anyway, and this is only ever called during render.
   */
  function headerFor(key: string): FilterableTHProps {
    const props = filters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;
    const sortControl = filter.sort;

    /*
     * 🔴 **The page reset, folded into the events that change the QUERY.**
     * Without it, `load()` fires for whatever page the reader was already on
     * against a result set that no longer has one: picking the single
     * `super_admin` from page 2 requested page 2 of a one-row response and drew
     * an EMPTY TABLE — which reads as "no super admins", the exact wrong
     * conclusion the funnel was opened to avoid, and the reason a rare value is
     * the one worth filtering for. `ContactsPage` has done this since it gained
     * filters; this page did not, and it mattered more here the moment the Role
     * funnel started offering roles nobody on page 1 holds.
     *
     * ⚠️ **A page-scoped column deliberately does NOT reset.** It filters the
     * rows already loaded, so jumping to page 1 would throw away the very rows
     * being filtered. Only a server-scoped change re-queries — hence the
     * `filter.scope` test rather than resetting on every tick.
     */
    /*
     * ⚠️ Was `withPageReset`, and it no longer resets one: the directory is
     * loaded whole, so there is no page to return to. Renamed rather than left
     * with a name describing machinery that has gone — a wrapper still exists
     * because it threads the sort control through, which is all it now does.
     */
    const withSort: ColumnFilterProps = {
      ...filter,
      ...(sortControl
        ? {
            sort: {
              direction: sortControl.direction,
              onSort: sortControl.onSort,
            },
          }
        : {}),
    };

    if (key !== 'groups') return { ...props, filter: withSort };
    return {
      ...props,
      filter: {
        ...withSort,
        options: groupFilterOptions(filter.value.selected),
        loading: groups.loading,
        /*
         * Disabled only when there is genuinely nothing to work with: no groups
         * read AND nothing ticked. With something ticked the funnel is the only
         * way back out of the filter, so it must stay open even on a failure.
         */
        disabled:
          (groups.failed || groups.unavailable) &&
          groups.records.length === 0 &&
          filter.value.selected.length === 0,
      },
    };
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listAllUsers(
          {
            ...(search ? { search } : {}),
            // Server-scoped column filters (role) and the server sort.
            ...filters.serverParams,
            ...(filters.sort
              ? { sort: filters.sort.key as UserSort, order: filters.sort.direction }
              : {}),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load users');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [search, filters.serverParams, filters.sort],
  );

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteUser(pendingDelete.id);
      setPendingDelete(null);
      /* Always a reload now: the directory is unpaginated, so deleting the last
       * row on screen cannot strand anybody on a page that no longer exists. */
      await load();
    } catch (caught) {
      const status = caught instanceof ApiError ? caught.status : 0;
      setDeleteError(
        status === 403
          ? `${denialReason(ability, 'delete', 'users') ?? 'The server refused that delete.'} An account also cannot delete itself.`
          : status === 409
            ? 'This is the last remaining administrator, so the account cannot be removed.'
            : caught instanceof Error
              ? caught.message
              : 'Delete failed',
      );
    } finally {
      setDeleting(false);
    }
  }

  function clearSearch() {
    setSearch('');
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: User) {
    setEditing(row);
    setFormOpen(true);
  }

  /*
   * An edit reloads the page in place. A create can land anywhere in the sort,
   * so page 1 is the only place it is reliably visible — and changing `page`
   * re-runs the load effect on its own, hence the either/or.
   */
  /*
   * An inline membership save replaces the ONE row in place from the server's
   * own response, rather than calling `load()`.
   *
   * ⚠️ A refetch would be wrong here, not merely wasteful: `load()` is async, so
   * between the draft being dropped and the new page arriving the control would
   * re-read the STALE record and show the membership the user just changed away
   * from. A visible flick to the old value reads as a failed save on a control
   * that had in fact succeeded.
   *
   * Only `groups` can have changed, but the whole record is taken — it is what
   * the server says the user now is, and copying one key across would invent a
   * record that exists nowhere.
   */
  const handleGroupsSaved = useCallback((updated: User) => {
    setResult((current) =>
      current === null
        ? current
        : {
            ...current,
            data: current.data.map((row) => (row.id === updated.id ? updated : row)),
          },
    );
  }, []);

  function handleSaved(saved: User) {
    const wasCreate = editing === null;
    /*
     * Editing your own account changes what the shell's account menu shows, and
     * that reads from the auth context rather than this page's rows — so the
     * name, email or role there would stay stale until a reload without this.
     */
    if (!wasCreate && editing?.id === user?.id) void refreshUser();
    /*
     * A create hands straight on to the onboarding email, which is the next
     * thing the admin has to do and the whole reason the account exists.
     *
     * ⚠️ **It OPENS the composer; it does not send anything.** The send still
     * needs the temporary password typed and its own confirmation — an email
     * that leaves SEED must never be a side effect of saving a record. And it
     * is gated: below the admin tier the composer is not offered here either,
     * matching the row action, so nobody meets a screen they cannot use.
     */
    if (wasCreate && canManageAccess) setOnboardingFor(saved);
    /* A new user used to need a jump to page 1 to be visible; with every row
     * loaded, a reload is all it takes. */
    void load();
  }

  /*
   * The API refuses to let an admin delete their own account (403). Expressing
   * that in the UI beats letting someone click and be rejected — the 403 is
   * still handled below in case the rule ever fires for another reason.
   */
  const canDelete = (rowId: number) => canDeleteUsers && rowId !== user?.id;

  /*
   * The actions column carries BOTH Edit and Delete, so it must appear when the
   * person can do EITHER — gating the whole cell on `canDeleteUsers` would hide
   * Edit from every write role below admin. Each button gates on its own
   * permission inside the cell, and the desktop table and stacked list below md
   * must agree on this.
   *
   * Head, body and skeleton all read this one value — a skeleton with a
   * different column count from the real table is a visible layout jump.
   *
   * ⚠️ It is unconditionally `true`, deliberately: the cell also carries
   * Targets, which every signed-in person can open for THEMSELVES.
   */
  const showRowActions = true;

  /*
   * ── COLUMN LAYOUT ────────────────────────────────────────────────────────
   *
   * ⚠️ **`showRowActions` decides whether `actions` is a COLUMN AT ALL, not
   * whether a rendered column is filled in.** Were the lane ever withheld, the
   * key would simply be absent from the layout — so it drops out of the manager
   * (no dead switch for a column that cannot appear) and out of the width floor
   * (which would otherwise stay 224px wider than the table it describes) in one
   * move rather than three.
   */
  const columnKeys = useMemo(
    () => (showRowActions ? ALL_COLUMN_KEYS : ALL_COLUMN_KEYS.filter((key) => key !== ACTIONS_KEY)),
    [showRowActions],
  );

  const layout = useColumnLayout(COLUMN_SCOPE, columnKeys, LOCKED_COLUMN_KEYS);
  const { frozenSideOf } = layout;

  const visibleColumns = useMemo(
    () =>
      layout.visibleKeys.flatMap((key) => {
        const column = COLUMN_BY_KEY.get(key);
        return column ? [column] : [];
      }),
    [layout.visibleKeys],
  );

  /**
   * The width FLOOR, summed over the columns actually on screen.
   *
   * At the full six it is 896. ⚠️ *SUPERSEDED: "At the full five it is 768 —
   * byte for byte the `min-w-[768px]` this table carried before the manager
   * existed."* Last active added 128 on 12 September 2026; the reduce is what
   * computes it, so the figure moved here with no arithmetic to keep in step.
   * Hiding a column takes its share out, so a five-column table stops reserving
   * room for a column that is not there.
   */
  const tableMinWidth = useMemo(
    () => visibleColumns.reduce((sum, column) => sum + column.minWidth, 0),
    [visibleColumns],
  );

  /*
   * The header row, for measuring frozen offsets.
   *
   * ✅ **AN ORDINARY `ref` ON `TR`.** *SUPERSEDED: "A LIVE GETTER RATHER THAN A
   * `ref` ON `TR`, AND THAT IS FORCED — `TR` is a function component whose props
   * are `HTMLAttributes` plus `interactive`, neither of which carries `ref` …
   * Widening `TR` is a shared-primitive change and not this page's to make."*
   * `TRProps` declares `ref?: Ref<HTMLTableRowElement>` as of 2026-09-12, so the
   * getter and the `querySelector` it hid are both gone.
   *
   * ⚠️ **The old note's objection to a plain `useRef` — that the hook might
   * measure before the row was recorded — does not apply to a real `ref`.**
   * React attaches a host element's ref in the commit's layout phase, child
   * before parent, so this row is attached before any layout effect *this*
   * component registers. And the two `TableFrame` mounts (skeleton, rows) hand
   * the hook a DIFFERENT element when the body swaps; `useFrozenOffsets` now
   * tracks that element identity itself and re-measures, which is what the page
   * used to have to arrange from outside.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);

  const frozenOffsets = useFrozenOffsets(headRowRef, layout.visibleKeys, layout.frozenSideOf);

  /**
   * The freeze props for one cell.
   *
   * ⚠️ **The offset is only ever an inline style** — `Table` applies it through
   * `frozenOffset`, because a computed `left-[…]`/`right-[…]` class emits no
   * rule. `noUncheckedIndexedAccess` is on, so a key with no measurement yet is
   * `undefined` here and `Table` falls back to 0, which is right for the first
   * frozen column on either edge and corrected on the next measure for the
   * rest.
   */
  const frozenPropsFor = useCallback(
    (key: string): FrozenCellProps => ({
      frozen: frozenSideOf(key),
      frozenOffset: frozenOffsets[key],
    }),
    // Destructured above rather than depending on `layout.frozenSideOf`, which
    // `oxlint` reads as a missing `layout`. The controller is memoised, so the
    // narrower dependency bought nothing and the warning was the dangerous kind
    // — a false positive that teaches a reader to wave the same rule away
    // somewhere it is real.
    [frozenSideOf, frozenOffsets],
  );

  /**
   * Who may open somebody's targets: your own always, anybody else's only as
   * an admin. Targets are salary-adjacent.
   *
   * ⚠️ This is a VIEW, not access control. `/targets` carries no `requireRole`
   * — the backend gates it on permission sets with a scope, like `/tasks` and
   * `/notes` — so hiding the button does not stop a hand-written request. The
   * server's refusal is the boundary; never describe this to a user as a
   * permission.
   *
   * `?subjectId=` answers a 403 rather than narrowing when the caller lacks the
   * audit grant, so hiding the control is the honest response — the modal still
   * renders the 403 for when a role changes under a live token (access tokens
   * last 5.28 days): the button is the optimisation, the refusal is the
   * guarantee.
   */
  /*
   * 2026-10-01: the audit grant decides, not the tier — `/targets?subjectId=`
   * names somebody else only with `targets.read` at `all` scope (super_admin
   * holds it by the resolver's bypass). *SUPERSEDED: `ability.isAdmin`.*
   */
  const seesEveryonesTargets =
    ability.isSuperAdmin || ability.scopeOf('read', 'targets') === 'all';
  const canSeeTargetsOf = useCallback(
    (row: User) => seesEveryonesTargets || row.id === user?.id,
    [seesEveryonesTargets, user?.id],
  );

  /*
   * Support sessions — acting as somebody else to reproduce what they see.
   *
   * TWO gates, and they are different questions answered in different ways:
   *
   *   - `canOfferSupportSessions` is the TIER, and it withholds the affordance
   *     entirely. `/users` is readable by every authenticated role, so a
   *     `viewer` must not meet a control they can never use on every row of a
   *     page they legitimately opened. This is the only thing on this page the
   *     tier hides; nothing else here widens or narrows because of it.
   *
   *   - `supportSessionDenial` is the PAIRING, and it disables with a reason —
   *     your own row, and another super administrator's row, both of which the
   *     server refuses. Disabled rather than absent, on purpose: a missing
   *     button teaches nobody the rule and sends them hunting, while a held-down
   *     one beside its sentence says what the rule is. The refusals are still
   *     handled inside the dialog, because a role can change under an access
   *     token that lasts over five days.
   *
   * Both live in `@/lib/support-session` so this page, the Settings search and
   * the dialog itself cannot come to different conclusions about one rule.
   */
  const showSupportAction = canOfferSupportSessions(ability);
  const supportDenialFor = useCallback(
    (row: User) => supportSessionDenial(ability, user, row, supportSession),
    [ability, user, supportSession],
  );

  /*
   * How many buttons the shimmer draws. Targets is per-row (your own, or any
   * row as an admin) and cannot be known before the rows exist, so it is
   * assumed present — an admin is who normally waits on this page, and drawing
   * one too many costs a slightly wide shimmer where drawing one too few costs
   * a jump. `Act as` is a tier decision and IS known up front; Edit, the
   * onboarding email and Delete are abilities, likewise.
   *
   * ⚠️ **Onboarding email counts TWICE over `canUpdateUsers`, once with Edit** —
   * both buttons sit behind the same ability, so the term is `2` rather than a
   * second `? 1 : 0` that would read as a different gate.
   */
  const skeletonActions =
    1 +
    (showSupportAction ? 1 : 0) +
    (canManageAccess ? 1 : 0) +
    (canUpdateUsers ? 2 : 0) +
    (canDeleteUsers ? 1 : 0);

  /**
   * One body cell, chosen by column KEY.
   *
   * ⚠️ **Keyed rather than positional, because the order is the reader's now.**
   * A switch on the key survives a column being moved or hidden; a fixed run of
   * `<TD>`s in source order does not, and its failure is silent — the header
   * says one thing and the cells under it say another.
   */
  const renderBodyCell = (column: UsersColumn, row: User) => {
    const frozen = frozenPropsFor(column.key);
    switch (column.key) {
      case 'name':
        return (
          <TD key={column.key} {...frozen}>
            <UserIdentity row={row} size="sm" />
          </TD>
        );
      case 'role':
        return (
          <TD key={column.key} {...frozen}>
            <RoleBadge role={row.role} />
          </TD>
        );
      case 'groups':
        return (
          <TD key={column.key} {...frozen}>
            {/* ⛔ The ONLY membership editor on this surface — see
                `LOCKED_COLUMN_KEYS`. The stacked list below md carries the
                same one; the two cannot disagree about a user's groups. */}
            <InlineUserGroups
              row={row}
              groups={groups}
              canUpdate={canUpdateUsers}
              denial={denialReason(ability, 'update', 'users')}
              onSaved={handleGroupsSaved}
            />
          </TD>
        );
      case 'id':
        return (
          <TD key={column.key} numeric className="font-mono text-xs text-fg-subtle" {...frozen}>
            {row.id}
          </TD>
        );
      case LAST_ACTIVE_KEY:
        return (
          <TD key={column.key} {...frozen}>
            <LastActive at={row.lastActiveAt} />
          </TD>
        );
      case ACTIONS_KEY:
        return (
          <TD key={column.key} numeric {...frozen}>
            <div className="flex justify-end gap-1.5">
              {canSeeTargetsOf(row) && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  leadingIcon={<TargetIcon />}
                  aria-label={`Targets for ${row.name}`}
                  title={`Targets for ${row.name}`}
                  onClick={() => setTargetsFor(row)}
                />
              )}
              {showSupportAction && (
                /*
                 * `title` carries the DENIAL when there is one and the action
                 * otherwise — a disabled button has `pointer-events: none`, so
                 * neither tooltip reaches the reader on hover; the `aria-label`
                 * is what a screen reader gets, and it names the person either
                 * way.
                 */
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  leadingIcon={<LifebuoyIcon />}
                  aria-label={`Act as ${row.name}`}
                  title={supportDenialFor(row) ?? `Act as ${row.name}`}
                  disabled={supportDenialFor(row) !== null}
                  onClick={() => setSupportFor(row)}
                />
              )}
              {canManageAccess && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  leadingIcon={<MailIcon />}
                  aria-label={`Onboarding email for ${row.name}`}
                  title={`Onboarding email for ${row.name}`}
                  onClick={() => setOnboardingFor(row)}
                />
              )}
              {canUpdateUsers && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  leadingIcon={<BellIcon />}
                  aria-label={`Notifications for ${row.name}`}
                  title={`Notifications for ${row.name}`}
                  onClick={() => openNotifications(row)}
                />
              )}
              {canUpdateUsers && (
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  leadingIcon={<PencilIcon />}
                  aria-label={`Edit ${row.name}`}
                  title={`Edit ${row.name}`}
                  onClick={() => openEdit(row)}
                />
              )}
              {canDelete(row.id) && (
                <Button
                  variant="danger"
                  size="sm"
                  iconOnly
                  leadingIcon={<TrashIcon />}
                  aria-label={`Delete ${row.name}`}
                  title={`Delete ${row.name}`}
                  onClick={() => {
                    setDeleteError(null);
                    setPendingDelete(row);
                  }}
                />
              )}
            </div>
          </TD>
        );
      default:
        return null;
    }
  };

  /*
   * ⚠️ The WHOLE directory is loaded — `api.listAllUsers` pages until it is
   * complete — so `total` is the number of rows in hand and there is no page
   * to be on. Owner's instruction, 12 September 2026.
   *
   * ✅ **This retires the page-scoped caveats the column filters carried.** The
   * Role funnel used to offer only the roles the loaded page happened to hold,
   * and `id` and `name` filtered the page rather than the directory — the
   * "vocabulary derived from the current page" failure this file records. With
   * every row present, page-scoped and directory-scoped are the same set.
   */
  const total = result?.meta.total ?? 0;
  const initialLoad = loading && !result;

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'user' : 'users'}${search ? ` matching “${search}”` : ''}`;

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Users"
        actions={
          canCreateUsers ? (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={openCreate}>
              Invite user
            </Button>
          ) : undefined
        }
      />

      <Panel>
        <PanelHeader
          title="Directory"
          description={countLabel}
          actions={
            <>
              <div className="w-full sm:w-64">
                <Field label="Search users" hideLabel>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="search"
                      placeholder="Search by name or email…"
                      leadingIcon={<SearchIcon />}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  )}
                </Field>
              </div>
              {/*
                ⚠️ **Hidden below `md`, and wrapped rather than classed.** Below
                md the rows are a stacked list with no columns at all, so the
                control would arrange something that is not on screen. The
                `hidden md:block` sits on a WRAPPER because `Button` bakes in
                `inline-flex`: `hidden` and `inline-flex` are the same CSS
                property, so passing `hidden` through `className` would race the
                emitted stylesheet's order rather than override anything.
              */}
              {/*
                ⚠️ **NO `pinnedKeys`, AND THAT IS DELIBERATE RATHER THAN AN
                OMISSION.** `pinnedKeys` names the columns the page renders with
                `pinned` — `sticky right-0` — whose edge is therefore already
                decided and not the reader's to choose. **This table renders no
                `pinned` cell at all**: it adopted the managed freeze without
                pinning, so the actions lane is an ordinary right-hand column
                that a reader may freeze like any other. Naming it here would
                strip its freeze controls and hand `useFrozenOffsets` a width to
                reserve at an edge nothing occupies.

                ⚠️ **`pinnedKeys` is NOT `lockedKeys`.** Locked is about hiding
                and reordering; pinned is about which edge a cell sits at.
                `groups` is locked and must stay freezable — it is the column a
                reader is most likely to want pinned to an edge while they scroll.
              */}
              <div className="hidden md:block">
                <ColumnManager
                  layout={layout}
                  labelOf={columnLabel}
                  lockedKeys={LOCKED_COLUMN_KEYS}
                  lockReasonOf={lockReasonOf}
                />
              </div>
            </>
          }
        />

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {filters.activeCount > 0 && (
          /*
           * ⚠️ The count that stood here — "N column filters applied. X of the
           * Y rows loaded here are hidden." — went on 2026-09-05 as a
           * hidden-rows/truncation note. The button remains, so the filters are
           * still clearable; how many rows they hide is no longer said.
           */
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 border-b border-border bg-surface-sunken px-4 py-2">
            <Button variant="secondary" size="sm" onClick={filters.clearAll}>
              Clear column filters
            </Button>
          </div>
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            className="m-4 mb-0"
            title={<>Couldn&rsquo;t load users.</>}
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below md a table cannot fit 320px, so rows become a stacked list. */}
          <div className="md:hidden">
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              <EmptyStateSlot
                search={search}
                onClear={clearSearch}
                hasError={Boolean(error)}
                columnFiltered={rows.length > 0}
                onClearFilters={filters.clearAll}
              />
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <UserIdentity row={row} size="md" />
                      <RoleBadge role={row.role} />
                    </div>
                    {/* Must match the desktop table's Groups column — the two
                        surfaces cannot disagree about a user's membership. */}
                    {(canUpdateUsers || groupsOf(row).length > 0) && (
                      <div className="mt-2">
                        <InlineUserGroups
                          row={row}
                          groups={groups}
                          canUpdate={canUpdateUsers}
                          denial={denialReason(ability, 'update', 'users')}
                          onSaved={handleGroupsSaved}
                        />
                      </div>
                    )}
                    {/* flex-wrap: the cell seats up to four labelled buttons
                        now, and at 320 they have to be able to leave the id's
                        row rather than pushing the card wider than the page. */}
                    <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3">
                      {/*
                        ⚠️ **Last active is carried here as well as in the table
                        column, and it has to be.** The `Columns` manager is
                        `hidden md:block` and this list has no columns at all,
                        so a reader below md would otherwise have no way to see
                        the field — and a value reachable only above a
                        breakpoint is indistinguishable from one that was never
                        added. Labelled, because there is no header above it to
                        say what the phrase means.
                      */}
                      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
                        <span className="font-mono text-fg-subtle tabular">#{row.id}</span>
                        <span className="text-fg-muted">
                          {LAST_ACTIVE_LABEL} <LastActive at={row.lastActiveAt} />
                        </span>
                      </div>
                      {/*
                        Targets sits outside the `canUpdateUsers` gate on
                        purpose — it is not a user write, and has its own rule,
                        `canSeeTargetsOf`. The desktop cell below must use both
                        gates the same way.
                      */}
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {canSeeTargetsOf(row) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          leadingIcon={<TargetIcon />}
                          onClick={() => setTargetsFor(row)}
                        >
                          Targets
                        </Button>
                        )}
                        {/* Must match the desktop cell below — the two
                            surfaces cannot disagree about what a row can do. */}
                        {showSupportAction && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<LifebuoyIcon />}
                            disabled={supportDenialFor(row) !== null}
                            title={supportDenialFor(row) ?? undefined}
                            onClick={() => setSupportFor(row)}
                          >
                            Act as
                          </Button>
                        )}
                        {/*
                          ⚠️ ABSENT below the admin tier rather than disabled.
                          `/users` is readable by every authenticated role, so a
                          reader who may never send must not meet this control
                          on every row of a page they legitimately opened —
                          the same rule `Act as` follows above. Must match the
                          desktop cell.
                        */}
                        {canManageAccess && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<MailIcon />}
                            onClick={() => setOnboardingFor(row)}
                          >
                            Onboarding email
                          </Button>
                        )}
                        {canUpdateUsers && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<BellIcon />}
                            onClick={() => openNotifications(row)}
                          >
                            Notifications
                          </Button>
                        )}
                        {canUpdateUsers && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete(row.id) && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => { setDeleteError(null); setPendingDelete(row); }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {initialLoad ? (
              <TableFrame minWidth={tableMinWidth}>
                <UsersTableHead
                  columns={visibleColumns}
                  headerFor={headerFor}
                  frozenPropsFor={frozenPropsFor}
                  headRowRef={headRowRef}
                />
                <TableSkeleton
                  columns={visibleColumns}
                  actionCount={skeletonActions}
                  frozenPropsFor={frozenPropsFor}
                />
              </TableFrame>
            ) : visibleRows.length === 0 ? (
              <EmptyStateSlot
                search={search}
                onClear={clearSearch}
                hasError={Boolean(error)}
                columnFiltered={rows.length > 0}
                onClearFilters={filters.clearAll}
              />
            ) : (
              <TableFrame minWidth={tableMinWidth}>
                <UsersTableHead
                  columns={visibleColumns}
                  headerFor={headerFor}
                  frozenPropsFor={frozenPropsFor}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {visibleRows.map((row) => (
                    <TR key={row.id} interactive>
                      {visibleColumns.map((column) => renderBodyCell(column, row))}
                    </TR>
                  ))}
                </TBody>
              </TableFrame>
            )}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {/* No pager and no range: every user is on screen, so the only
                * honest figures are how many are shown and how many the
                * filters are hiding. A "1–15 of 15" range over an unpaginated
                * list describes a control that no longer exists. */}
              {total === 0
                ? 'No users to show'
                : hiddenByFilters > 0
                  ? `Showing ${visibleRows.length} of ${total} users · ${hiddenByFilters} hidden by filters`
                  : `Showing all ${total} ${total === 1 ? 'user' : 'users'}`}
            </p>
          </PanelFooter>
        )}
      </Panel>

      <UserFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        {...(editing ? { user: editing } : {})}
        currentUserId={user?.id ?? null}
        onSaved={handleSaved}
      />

      {/*
        Per-user targets. A modal rather than a section on a detail page because
        THERE IS NO `/users/:id` ROUTE and no `getUser` in `api.ts` — `/users` is
        index-only, deliberately, so anything per-user has to be a modal or an
        inline block. Mounted unconditionally and closed by `user === null`, the
        same shape as the form modal above.
      */}
      <UserTargetsModal
        open={targetsFor !== null}
        user={targetsFor}
        currentUserId={user?.id ?? null}
        onClose={() => setTargetsFor(null)}
      />

      {/*
        The support session. Mounted unconditionally and closed by
        `user === null`, the same shape as the two modals above it — and a modal
        for the same structural reason: `/users` is index-only, so there is no
        `/users/:id` page for this to live on.
      */}
      <SupportSessionDialog user={supportFor} onClose={() => setSupportFor(null)} />

      {/*
        The onboarding email. Mounted unconditionally and closed by
        `user === null`, the same shape as the three modals above it — and a
        modal for the same structural reason: `/users` is index-only, so there
        is no `/users/:id` page for this to live on.

        ⚠️ `canManage` is passed rather than gating the mount, so a refusal
        arriving under a live access token (they last days) is NAMED inside the
        composer instead of the screen simply being empty. The row action and
        the post-create hand-off are already withheld below the admin tier, so
        this is the change-under-a-live-token case, not the everyday one.
      */}
      <OnboardingEmailModal
        open={onboardingFor !== null}
        user={onboardingFor}
        canManage={canManageAccess}
        denial={denialReason(ability, 'manage-access', 'users')}
        onClose={() => setOnboardingFor(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete user?'}
        description={
          <>
            {pendingDelete?.name ?? 'This user'}
            {pendingDelete?.email ? ` (${pendingDelete.email})` : ''} will be deleted and can no
            longer sign in. This can't be undone.
          </>
        }
        confirmLabel="Delete user"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
      />
    </>
  );
}

/**
 * Every column WITH A `ColumnSpec` gets a filter funnel — which is all of them
 * but `actions` and `lastActiveAt`, and the branch below says why. `name`,
 * `role`, `groups` and `id` get a sort arrow — each carries a `sortKey`, and
 * `load()` forwards it as the server's `sort`/`order` pair. *SUPERSEDED
 * 2026-10-09: "Only `name` gets a sort arrow … `role` and `id` have none and
 * must stay unsorted."*
 *
 * ⚠️ A `sortKey` is a claim about the SERVER, not about this table. The query
 * schema is strict, so a column given a `sortKey` outside `UserSort` sends a
 * value the API answers with a 422 — the list goes blank rather than merely
 * unsorted. Each key is pinned with `satisfies UserSort` for exactly that.
 */
function UsersTableHead({
  columns,
  headerFor,
  frozenPropsFor,
  headRowRef,
}: {
  /** The VISIBLE columns, in layout order — see `USERS_TABLE_COLUMNS`. */
  columns: readonly UsersColumn[];
  headerFor: TableFilters<User>['headerFor'];
  frozenPropsFor: (key: string) => FrozenCellProps;
  /**
   * The element `useFrozenOffsets` measures. It lands on the row rather than on
   * a wrapper because the offsets are the summed rendered widths of the header
   * CELLS, which the hook reads off this row's children.
   */
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {columns.map((column) =>
          /*
           * ⚠️ **TWO columns have no `ColumnSpec`**, so they have no `headerFor`
           * entry to spread and get a plain label instead — no funnel, and so
           * no `aria-sort`, which `FilterableTH` only claims where a sort
           * control is passed.
           *
           *   - `actions` has no VALUE to filter or sort on.
           *   - `lastActiveAt` has one and the SERVER cannot narrow or order by
           *     it: `UserListParams` declares no such key and `UserSort` has no
           *     such member, and the query schema is strict, so a funnel here
           *     would either 422 the whole list or filter the page against a
           *     vocabulary of rendered phrases. See `LAST_ACTIVE_KEY`.
           *
           * ⚠️ **Tested against `FILTERED_COLUMN_KEYS`, not by naming the two.**
           * The set is derived from `USER_COLUMNS`, so a column that gains a
           * spec gains its funnel here with no second edit — where a hand-kept
           * pair of key comparisons goes stale silently.
           */
          !FILTERED_COLUMN_KEYS.has(column.key) ? (
            <FilterableTH
              key={column.key}
              label={columnLabel(column.key)}
              numeric={column.numeric}
              className={column.className}
              {...frozenPropsFor(column.key)}
            />
          ) : (
            <FilterableTH
              key={column.key}
              {...headerFor(column.key)}
              className={column.className}
              {...frozenPropsFor(column.key)}
            />
          ),
        )}
      </TR>
    </THead>
  );
}

function EmptyStateSlot({
  search,
  onClear,
  hasError,
  columnFiltered,
  onClearFilters,
}: {
  search: string;
  onClear: () => void;
  hasError: boolean;
  /** True when the page did load rows and the column filters hid all of them. */
  columnFiltered: boolean;
  onClearFilters: () => void;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<UsersIcon />}
        title="Nothing to show"
        description="Couldn’t load the directory — see the message above."
      />
    );
  }

  if (columnFiltered) {
    return (
      <EmptyState
        icon={<UsersIcon />}
        title="No rows on this page match the column filters"
        description="Filters apply to this page only — a match may sit on another."
        action={
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={<UsersIcon />}
      title={search ? 'No users match that search' : 'No users yet'}
      description={
        search
          ? 'Try a different name or email.'
          : 'Nobody has been added yet.'
      }
      action={
        search ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search
          </Button>
        ) : undefined
      }
    />
  );
}
